const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const RESPONSE_FIELD = 'cf-turnstile-response'

// If the check hasn't finished this long after a submit, send the form anyway and let the server answer.
const SUBMIT_WAIT_MS = 15000

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve()
  if (window.__signwithTurnstileLoading) return window.__signwithTurnstileLoading

  window.__signwithTurnstileLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      window.__signwithTurnstileLoading = null
      reject(new Error('Turnstile failed to load'))
    }
    document.head.appendChild(script)
  })

  return window.__signwithTurnstileLoading
}

// Cloudflare Turnstile on the signup email step (see app/services/turnstile.rb). Lives in the form; the widget
// itself is drawn in the element named by data-slot when the page has one (below the email box), else in place.
// It's invisible unless Cloudflare wants the visitor to tick a box. On submit the token goes into the form as
// cf-turnstile-response; a submit before the token is ready waits for it, then goes through.
export default class TurnstileWidget extends HTMLElement {
  connectedCallback () {
    this.form = this.closest('form')
    this.form?.addEventListener('submit', this.onSubmit)

    loadTurnstile()
      .then(() => this.render())
      .catch(() => this.giveUp())
  }

  disconnectedCallback () {
    this.form?.removeEventListener('submit', this.onSubmit)
    clearTimeout(this.waitTimer)
    if (this.widgetId != null) window.turnstile?.remove(this.widgetId)
    this.container?.classList.remove('is-interactive')
    this.widgetId = null
    this.pendingSubmit = null
  }

  render () {
    if (!this.isConnected || this.widgetId != null) return

    this.container = (this.dataset.slot && document.querySelector(this.dataset.slot)) || this
    this.widgetId = window.turnstile.render(this.container, {
      sitekey: this.dataset.siteKey,
      action: this.dataset.action,
      appearance: 'interaction-only',
      'response-field': false,
      callback: () => this.submitPending(),
      'error-callback': () => this.giveUp(),
      'before-interactive-callback': () => this.container.classList.add('is-interactive'),
      'after-interactive-callback': () => this.container.classList.remove('is-interactive')
    })
  }

  token () {
    return this.widgetId != null ? window.turnstile?.getResponse(this.widgetId) : ''
  }

  onSubmit = (event) => {
    const token = this.token()

    if (token || this.failed) {
      this.field().value = token || ''
      return
    }

    event.preventDefault()
    this.pendingSubmit = { submitter: event.submitter }
    clearTimeout(this.waitTimer)
    this.waitTimer = setTimeout(() => this.giveUp(), SUBMIT_WAIT_MS)
  }

  field () {
    let field = this.form.querySelector(`input[name="${RESPONSE_FIELD}"]`)
    if (!field) {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = RESPONSE_FIELD
      this.form.appendChild(field)
    }
    return field
  }

  submitPending () {
    const pending = this.pendingSubmit
    if (!pending || !this.form?.isConnected) return

    this.pendingSubmit = null
    clearTimeout(this.waitTimer)
    this.form.requestSubmit(pending.submitter?.form === this.form ? pending.submitter : undefined)
  }

  // Script blocked, widget error or a slow check: stop holding the form. The server then shows why it
  // couldn't sign the visitor up (and Google sign-up still works).
  giveUp () {
    this.failed = true
    this.submitPending()
  }
}
